/**
 * @deprecated This file is maintained for backward compatibility only.
 * For new code, use dynamicSectionProcessors.ts which provides an automated
 * approach that doesn't require manual updates when schema changes occur.
 *
 * This file is now a pure delegation layer that forwards all processing
 * to the dynamicSectionProcessors system while maintaining the legacy API.
 */

import {
  StoryCardType,
  StoryGreetingCardType,
} from 'src/home_page/components/CardStoriesRibbon/CardStoriesRibbon'
import { GetHomePageData } from '../graphql/queries/__generated__/GetHomePage'
import { BentoGridSectionFragment } from '../queries'
import { CardTypeSectionFragment } from '../graphql/queries/__generated__/CardTypeSectionFragment'
import dynamicProcessors from '../dynamicSectionProcessors'

// Custom logger to avoid direct console calls
const logger = {
  /**
   * Log informational messages
   * @param {string} message The message to log
   */
  info: (message: string): void => {
    // Replace with appropriate logging mechanism
    // e.g., a logging library or silent operation
  },

  /**
   * Log error messages
   * @param {string} message The error message
   * @param {Error|any} [err] Optional error object
   */
  error: (message: string, err?: Error | any): void => {
    // Replace with appropriate error logging
    // e.g., a logging library with error levels
  },

  /**
   * Log warning messages
   * @param {string} message The warning message
   */
  warn: (message: string): void => {
    // Replace with appropriate warning logging
  },
}

// Re-export resolveUrl from dynamicSectionProcessors
export const resolveUrl = dynamicProcessors.resolveUrl

export type SectionProcessor<T> = (
  section: any,
  options: {
    handleSendFreeCard: () => void
    abs: (u?: string | null) => string | undefined
  },
) => T

export interface SectionProcessorRegistry {
  [key: string]: SectionProcessor<any>
}

export type Slide = {
  title: string
  subtitle: string
  imageUrl: string
  action: { title: string; onClick: () => void }
  themeBackgroundColor?: string | null
  themeColor?: string | null
  titleColorOverride?: string | null
  buttonColorOverride?: string | null
}

export type StoryItem = StoryGreetingCardType | StoryCardType

/**
 * Uses dynamic processors to process banner sections, but transforms the result
 * to maintain the expected Slide[] format for backward compatibility
 */
export const processBannerSection: SectionProcessor<Slide[]> = (
  section,
  { handleSendFreeCard, abs },
) => {
  // Use dynamic processor to get base processed data
  const processor = dynamicProcessors.getSectionProcessor(
    'ComponentSectionsBannerSlider',
  )
  const processedSection = processor(section, { abs })

  // Transform the processed data to the expected format with proper type assertions
  const slides = (processedSection?.slides as any[]) || []
  return slides.map((s: any) => ({
    title: s.title || 'Welcome to SendOutCards',
    subtitle:
      s.description || 'Share your gratitude with beautiful cards and gifts',
    imageUrl:
      s.image?.url ||
      'https://soc-website-public.s3.us-west-2.amazonaws.com/static/soc-website/images/home-banners/Automated-image-16-9.png',
    action: s.button
      ? {
          title: s.button.label,
          onClick: (() => {
            switch (s.button.actionType || s.button.action) {
              case 'SEND_FREE_CARD':
                return handleSendFreeCard
              case 'OPEN_PRICING':
                return () => (window.location.href = '/app/pricing#packages')
              case 'OPEN_CATALOG':
                return () => (window.location.href = '/catalog')
              default:
                return () => {}
            }
          })(),
        }
      : {
          title: 'Explore Cards',
          onClick: () => (window.location.href = '/catalog'),
        },
    themeBackgroundColor: s.themeBgColor,
    themeColor: s.themeBgColor,
    titleColorOverride: s.button?.textColorOverride,
    buttonColorOverride: s.button?.backgroundColorOverride,
  }))
}

/**
 * Uses dynamic processors to process ribbon sections, but transforms the result
 * to maintain the expected StoryItem[] format for backward compatibility
 */
export const processRibbonStoriesSection: SectionProcessor<StoryItem[]> = (
  section,
  { abs },
) => {
  // Use dynamic processor to get base processed data
  const processor = dynamicProcessors.getSectionProcessor(
    'ComponentSectionsRibbonStories',
  )
  const processedSection = processor(section, { abs })

  const ribbonStory = (processedSection?.ribbonStory as any[]) || []
  return ribbonStory
    .map((item: any) => {
      // Handle card type stories
      if (item.frontImage !== undefined) {
        return {
          frontImage: item.frontImage,
          isHorizontal: Boolean(item.isHorizontal),
        } as StoryGreetingCardType
      }

      return {
        title: item.title || '',
        description: item.description || '',
        position: (item.position as 'top' | 'center' | 'bottom') || 'center',
      } as StoryCardType
    })
    .filter(Boolean)
}

/**
 * Uses dynamic processors to process card type sections, but transforms the result
 * to maintain the expected CardTypeSectionFragment format for backward compatibility
 */
export const processCardTypeSection: SectionProcessor<CardTypeSectionFragment> = (
  section,
  { abs },
) => {
  // Use dynamic processor to get base processed data
  const processor = dynamicProcessors.getSectionProcessor(
    'ComponentSectionsCardTypeSection',
  )
  const processedSection = processor(section, { abs })

  // Cast processed section to expected type and return
  return processedSection as CardTypeSectionFragment
}

/**
 * Uses dynamic processors to process bento grid sections, but transforms the result
 * to maintain the expected BentoGridSectionFragment[] format for backward compatibility
 */
export const processBentoGridSection: SectionProcessor<BentoGridSectionFragment[]> = (
  section,
  { abs },
) => {
  // Use dynamic processor to get base processed data
  const processor = dynamicProcessors.getSectionProcessor(
    'ComponentSectionsBentoGrid',
  )
  const processedSection = processor(section, { abs })

  // Return as array for backward compatibility
  return [processedSection as BentoGridSectionFragment]
}

/**
 * Uses dynamic processors to process bento sections, but transforms the result
 * to maintain the expected CardTypeSectionFragment[] format for backward compatibility
 */
export const processBentoSectionsSection: SectionProcessor<CardTypeSectionFragment[]> = (
  section,
  { abs },
) => {
  // Use dynamic processor to get base processed data
  const processor = dynamicProcessors.getSectionProcessor(
    'ComponentSectionsBentoSections',
  )
  const processedSection = processor(section, { abs })

  // Transform the processed data to the expected format with proper type assertions
  const bentoSections = (processedSection?.bentoSection as any[]) || []
  return bentoSections.map(
    (bentoSection: any) => bentoSection as CardTypeSectionFragment,
  )
}

/**
 * Uses dynamic processors to process video sections, but transforms the result
 * to maintain the expected format for backward compatibility
 */
export const processVideoBlockSection: SectionProcessor<any[]> = (
  section,
  { abs },
) => {
  // Use dynamic processor to get base processed data
  const processor = dynamicProcessors.getSectionProcessor(
    'ComponentSectionsVideoBlocks',
  )
  const processedSection = processor(section, { abs })

  // Return in the expected array format for backward compatibility
  return [
    {
      ...processedSection,
      videoBlock: processedSection?.videoBlock || [],
    },
  ]
}

export const sectionProcessors: SectionProcessorRegistry = {
  ComponentSectionsBannerSlider: processBannerSection,
  ComponentSectionsRibbonStories: processRibbonStoriesSection,
  ComponentSectionsCardType: processCardTypeSection,
  ComponentSectionsBentoGridSection: processBentoGridSection,
  ComponentSectionsBentoSections: processBentoSectionsSection,
  ComponentSectionsVideoBlocks: processVideoBlockSection,
}

/**
 * Type definition for the structure returned by processSections
 * This helps ensure type compatibility in the reducer
 */
export interface HomePageProcessedSections {
  slides: Slide[]
  stories: StoryItem[]
  cardTypes: CardTypeSectionFragment | { cards: any[]; imageDiffs: any[] }
  paperTypes: BentoGridSectionFragment[]
  bentoSections: CardTypeSectionFragment[]
}

/**
 * @deprecated Use dynamicProcessors.processSections instead
 * Process sections data from Strapi using dynamic processors
 * but return in the legacy format for backward compatibility
 */
export function processSections(
  data: GetHomePageData | null,
  handleSendFreeCard: () => void,
): HomePageProcessedSections {
  logger.info('[Strapi] Processing sections using dynamic processors')

  const strapiResult = data
    ? {
        data: {
          attributes: {
            Sections: {
              data:
                data.homepage?.sections?.map(section => ({
                  attributes: section,
                })) || [],
            },
          },
        },
      }
    : null

  const processedSections = strapiResult
    ? dynamicProcessors.processSections(strapiResult)
    : null

  // Return empty result if no sections processed
  if (!processedSections || !Array.isArray(processedSections)) {
    logger.warn('[Strapi] No sections processed')
    return {
      slides: [],
      stories: [],
      cardTypes: { cards: [], imageDiffs: [] },
      paperTypes: [],
      bentoSections: [],
    }
  }

  const initialResult: HomePageProcessedSections = {
    slides: [] as Slide[],
    stories: [] as StoryItem[],
    cardTypes: {
      __typename: 'ComponentSectionsCardType',
      id: '',
      gridTitle: '',
      gridDescription: '',
      gridSubtitle: '',
      isComponentRight: false,
      cards: [],
      imageDiffs: [],
    } as CardTypeSectionFragment | { cards: any[]; imageDiffs: any[] },
    paperTypes: [] as BentoGridSectionFragment[],
    bentoSections: [] as CardTypeSectionFragment[],
  }

  const finalResult = processedSections.reduce<HomePageProcessedSections>(
    (acc, section) => {
      if (!section) return acc

      const ensureArray = <T>(value: unknown): T[] => {
        return Array.isArray(value) ? value : []
      }

      // Process each section by its type using the appropriate processor
      switch (section?.__typename) {
        case 'ComponentSectionsBannerSlider':
          return {
            ...acc,
            slides: ensureArray<Slide>(section.slides || section.banners),
          }
        case 'ComponentSectionsRibbonStories':
          return {
            ...acc,
            stories: ensureArray<StoryItem>(section.stories),
          }
        case 'ComponentSectionsCardType':
          return {
            ...acc,
            cardTypes: section as CardTypeSectionFragment,
          }
        case 'ComponentSectionsBentoGridSection':
          return {
            ...acc,
            paperTypes: [section as BentoGridSectionFragment],
          }
        case 'ComponentSectionsBentoSections':
          return {
            ...acc,
            bentoSections: ensureArray<CardTypeSectionFragment>(
              section.bentoSection,
            ),
          }
        default:
          return acc
      }
    },
    initialResult,
  )

  return finalResult
}
